import { FileRemoveEvent, FileSelectEvent } from 'primeng/fileupload';
import {
  CustomFieldProps,
  DynamicField,
  DynamicForm,
  EditorFieldProps,
  FieldVariant,
  FileFieldProps,
  FileRepresentation,
  SelectFieldProps,
  SelectOption,
  TextFieldProps,
  DateFieldProps,
} from '../../../../components/form-builder/form-builder.types';
import { BudgetEstimateComponent } from '../../../../components/shared/budget-estimate/budget-estimate.component';
import { MultiFormAccordionComponent } from '../../../../components/multi-form-accordion/multi-form-accordion.component';
import { PaaRepository } from '../../../../store/paa-state/paa-state.repository';
import { ResponseDoc } from '@/types';
import { map, Observable, of } from 'rxjs';
import { PaaMilestoneFormComponent } from '../../milestone-form/milestone-form.component';

interface getPaaUpdatePlanFormObjectProps {
  store: PaaRepository;
  acOptions: SelectOption[];
  clearDirection?: () => void;
  download?: (file: FileRepresentation) => void;
  openFile?: (file: FileRepresentation) => void;
  loading?: Observable<boolean>;
  isUpdatable?: Observable<boolean>;
}

export const getPaaUpdatePlanFormObject = ({
  store,
  acOptions,
  clearDirection,
  download,
  openFile,
  isUpdatable,
}: getPaaUpdatePlanFormObjectProps): DynamicForm => {
  const isDisabled = isUpdatable?.pipe(map(value => !value));

  const refField: DynamicField<TextFieldProps> = {
    label: 'paa.form.ref',
    variant: FieldVariant.TEXT,
    isRequired: true,
    isDisabled: of(true),
    props: {
      value: store.getNestedObservable<string>('dto', 'ref'),
      onChange: (value: string) => {
        store.setNested('dto', 'ref', value);
      },
    },
  };

  const nomField: DynamicField<TextFieldProps> = {
    label: 'paa.form.name',
    variant: FieldVariant.TEXT,
    isRequired: true,
    isDisabled,
    error: store.getNestedObservable('errors', 'nom'),
    props: {
      value: store.getNestedObservable<string>('dto', 'nom'),
      onChange: (value: string) => {
        store.setNested('dto', 'nom', value);
      },
      placeholder: 'paa.form.placeholders.name',
    },
  };

  const yearField: DynamicField<DateFieldProps> = {
    label: 'paa.form.year',
    variant: FieldVariant.DATE,
    isRequired: true,
    isDisabled: of(true),
    error: store.getNestedObservable<string>('errors', 'annee'),
    props: {
      value: store.getNestedObservable<number>('dto', 'annee'),
      onChange: () => undefined,
      view: 'year',
      format: 'yy',
    },
  };

  const acOptionsField: DynamicField<SelectFieldProps> = {
    label: 'paa.form.ac_options',
    variant: FieldVariant.SELECT,
    isRequired: true,
    isDisabled: of(true),
    error: store.getNestedObservable<string>('errors', 'acId'),
    props: {
      value: store.getObservable('acsSelected'),
      onSelectChange: (value: SelectOption) => {
        store.set('acsSelected', value);
        store.setNested('dto', 'acId', value?.code);
        store.clearError('acId');
        clearDirection?.();
      },
      placeholder: 'paa.form.placeholders.ac_options',
      options: acOptions,
      filterEnabled: true,
    },
  };

  const milestoneField: DynamicField<CustomFieldProps> = {
    label: 'paa.form.milestone',
    variant: FieldVariant.CUSTOM,
    isRequired: true,
    props: {
      component: MultiFormAccordionComponent,
      injectKeys: ['accordionObject'],
      accordionObject: {
        titles: ['paa.form.milestone2'],
        component: PaaMilestoneFormComponent,
        injectKeys: ['isDisabled', 'acId'],
        isDisabled,
        acId: store.getNestedObservable<number>('dto', 'acId'),
        downSyndromeComponent: BudgetEstimateComponent,
        downSyndromeProps: {
          label: 'milestone.form.estimated_total_budget',
          value: store.getObservable<number>('totalEstimatedBudget'),
        },
      },
    },
  };

  const noteField: DynamicField<EditorFieldProps> = {
    label: 'Remarque',
    variant: FieldVariant.EDITOR,
    isDisabled,
    props: {
      height: '320px',
      value: store.getNestedObservable<string>('dto', 'remarque'),
      onChange: (value: string) => {
        store.setNested('dto', 'remarque', value);
        store.clearError('remarque');
      },
    },
  };

  const uploadField: DynamicField<FileFieldProps> = {
    label: 'Pièce jointe',
    variant: FieldVariant.FILE,
    isDisabled,
    props: {
      mode: 'advanced',
      accept: 'image/*, application/pdf',
      multiple: true,
      value: store.getNestedObservable<File[]>('dto', 'files'),
      onFileSelect: (event: FileSelectEvent) => {
        store.setNested('dto', 'paaFiles', event.currentFiles);
      },
      onFileRemoved: (event: FileRemoveEvent) => {
        store.setNested(
          'dto',
          'paaFiles',
          store.getNested<File[]>('dto', 'paaFiles').filter(file => file.name !== event.file.name)
        );
      },
      files: store
        .getNestedObservable<ResponseDoc[]>('response', 'docs')
        .pipe(
          map(docs =>
            docs?.map(doc => ({ id: doc.id, filename: doc.name, extension: doc.fileExtension, size: doc.fileSize, alfresco_id: doc.alfresco_id }))
          )
        ),
      onFileRepresentationChange: (file: ResponseDoc) => {
        store.setNested(
          'response',
          'docs',
          store.getNested<ResponseDoc[]>('response', 'docs').filter(doc => doc.id != file.id)
        );
        store.setNested(
          'dto',
          'docIds',
          store.getNested<number[]>('dto', 'docIds').filter(docId => docId !== file.id)
        );
      },
      onFileDownLoad: (file: FileRepresentation) => download?.(file),
      openFile: (file: FileRepresentation) => openFile?.(file),
      onAllRemoved: () => {
        store.setNested('response', 'docs', []);
        store.setNested('dto', 'docIds', []);
      },
    },
  };

  return {
    title: 'Plan annuel des achats de cette année',
    description: 'Gérez et suivez votre plan annuel des achats pour cette année, Modifiez, mettez  à jour ou visualisez.',
    orientation: 'horizontal',
    isHeaderHidden: true,
    grids: [
      {
        title: 'Information Avis',
        isHeaderHidden: true,
        gridItems: [
          {
            fields: [refField, acOptionsField],
          },
          {
            fields: [yearField, nomField],
          },
        ],
      },
      {
        title: 'Réalisation envisagée',
        isHeaderHidden: true,
        gridItems: [
          {
            fields: [milestoneField],
          },
        ],
      },
      {
        title: 'Entrée secondaire',
        gridItems: [
          {
            fields: [uploadField],
          },
          {
            fields: [noteField],
          },
        ],
      },
    ],
  };
};
